import CryptoJS from "crypto-js";
import DOMPurify from "dompurify";
import { SECURITY_CONFIG } from "./security";

/**
 * Sanitize input using DOMPurify for robust XSS protection
 * @param {string} value - Input value to sanitize
 * @param {object} options - DOMPurify options
 * @returns {string} Sanitized value
 */
export const sanitizeInput = (value, options = {}) => {
  if (typeof value !== "string") {
    return value;
  }

  // Default configuration - strip all HTML
  const defaultConfig = {
    ALLOWED_TAGS: SECURITY_CONFIG.INPUT.ALLOWED_TAGS,
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_DOM_IMPORT: false,
    FORCE_BODY: false,
  };

  const config = { ...defaultConfig, ...options };

  try {
    // Use DOMPurify to sanitize
    const sanitized = DOMPurify.sanitize(value, config);

    // Additional sanitization - remove any script-like patterns
    return sanitized
      .replace(/javascript:/gi, "")
      .replace(/on\w+\s*=/gi, "")
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
      .trim();
  } catch (error) {
    console.error("Error sanitizing input:", error);
    // Fallback to basic sanitization
    return value.replace(/<[^>]*>?/gm, "").trim();
  }
};

/**
 * Sanitize object properties recursively
 * @param {object} obj - Object to sanitize
 * @returns {object} Sanitized object
 */
export const sanitizeObject = (obj) => {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => sanitizeObject(item));
  }

  const sanitized = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];
      if (typeof value === "string") {
        sanitized[key] = sanitizeInput(value);
      } else if (typeof value === "object") {
        sanitized[key] = sanitizeObject(value);
      } else {
        sanitized[key] = value;
      }
    }
  }
  return sanitized;
};

/**
 * Validate and sanitize URL
 * @param {string} url - URL to validate
 * @returns {string|null} Sanitized URL or null if invalid
 */
export const sanitizeURL = (url) => {
  if (!url || typeof url !== "string") return null;

  try {
    const urlObj = new URL(url);
    // Only allow http and https protocols
    if (!["http:", "https:"].includes(urlObj.protocol)) {
      return null;
    }
    return urlObj.href;
  } catch (error) {
    return null;
  }
};

/**
 * Sanitize email address
 * @param {string} email - Email to sanitize
 * @returns {string} Sanitized email
 */
export const sanitizeEmail = (email) => {
  if (!email || typeof email !== "string") return "";

  return email
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9@._+-]/g, "");
};

export const validateFile = (file) => {
  if (!file) {
    return "No file selected.";
  }

  // Check for file type
  const validTypes = [
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/bmp",
    "image/webp",
  ];
  if (!validTypes.includes(file.type)) {
    return "Invalid file type. Only image files (jpeg, png, gif, bmp, webp) are allowed.";
  }

  // Check for file size (5MB limit)
  const maxSize = 5 * 1024 * 1024;
  if (file.size > maxSize) {
    return "File size exceeds the 5MB limit.";
  }

  const forbiddenExtensions = ["svg"];
  const extension = file.name.split(".").pop().toLowerCase();

  if (forbiddenExtensions.includes(extension)) {
    return "SVG files are not allowed.";
  }

  return null;
};

export const validatePassword = (password) => {
  if (!password) {
    return "Password is required.";
  }

  if (password.length < 8) {
    return "Password must be at least 8 characters long.";
  }

  if (password.length > 16) {
    return "Password must not exceed 16 characters.";
  }

  // Check for at least one uppercase letter
  if (!/[A-Z]/.test(password)) {
    return "Password must contain at least one uppercase letter.";
  }

  // Check for at least one lowercase letter
  if (!/[a-z]/.test(password)) {
    return "Password must contain at least one lowercase letter.";
  }

  // Check for at least one number
  if (!/[0-9]/.test(password)) {
    return "Password must contain at least one number.";
  }

  // Check for at least one special character/symbol
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    return "Password must contain at least one special character.";
  }

  return null;
};

/**
 * Check if string contains HTML tags
 * @param {string} value - String to check
 * @returns {boolean} True if contains HTML tags
 */
export const containsHTML = (value) => {
  if (!value || typeof value !== "string") return false;

  // Check for HTML tag patterns
  const htmlPattern = /<[^>]*>/g;
  return htmlPattern.test(value);
};

/**
 * Validate title field
 * @param {string} title - Title to validate
 * @param {object} options - Validation options
 * @returns {string|null} Error message or null if valid
 */
export const validateTitle = (title, options = {}) => {
  const { minLength = 3, maxLength = 100, fieldName = "Title" } = options;

  if (!title || !title.trim()) {
    return `${fieldName} is required`;
  }

  // Check for HTML tags
  if (containsHTML(title)) {
    return `${fieldName} cannot contain HTML tags`;
  }

  const trimmedTitle = title.trim();

  if (trimmedTitle.length < minLength) {
    return `${fieldName} must be at least ${minLength} characters`;
  }

  if (trimmedTitle.length > maxLength) {
    return `${fieldName} must not exceed ${maxLength} characters`;
  }

  // Allow only letters, numbers, spaces, and basic punctuation
  const validPattern = /^[a-zA-Z0-9\s\-_.,!?()'":&]+$/;
  if (!validPattern.test(trimmedTitle)) {
    return `${fieldName} contains invalid characters`;
  }

  return null;
};

/**
 * Validate description field
 * @param {string} description - Description to validate
 * @param {object} options - Validation options
 * @returns {string|null} Error message or null if valid
 */
export const validateDescription = (description, options = {}) => {
  const {
    minLength = 10,
    maxLength = 1000,
    fieldName = "Description",
    required = true,
  } = options;

  if (required && (!description || !description.trim())) {
    return `${fieldName} is required`;
  }

  // If not required and empty, skip validation
  if (!required && (!description || !description.trim())) {
    return null;
  }

  // Check for HTML tags
  if (containsHTML(description)) {
    return `${fieldName} cannot contain HTML tags`;
  }

  const trimmedDescription = description.trim();

  if (trimmedDescription.length < minLength) {
    return `${fieldName} must be at least ${minLength} characters`;
  }

  if (trimmedDescription.length > maxLength) {
    return `${fieldName} must not exceed ${maxLength} characters`;
  }

  // Allow letters, numbers, spaces, basic punctuation, and newlines
  const validPattern = /^[a-zA-Z0-9\s\-_.,!?()'":;&\n\r@#$%]+$/;
  if (!validPattern.test(trimmedDescription)) {
    return `${fieldName} contains invalid characters`;
  }

  return null;
};

/**
 * Validate coin name field
 * @param {string} name - Coin name to validate
 * @returns {string|null} Error message or null if valid
 */
export const validateCoinName = (name) => {
  if (!name || !name.trim()) {
    return "Coin Name is required";
  }

  // Check for HTML tags
  if (containsHTML(name)) {
    return "Coin Name cannot contain HTML tags";
  }

  const trimmedName = name.trim();

  if (trimmedName.length < 2) {
    return "Coin Name must be at least 2 characters";
  }

  if (trimmedName.length > 50) {
    return "Coin Name must not exceed 50 characters";
  }

  // Allow letters, numbers, spaces, and basic punctuation for crypto names
  const validPattern = /^[a-zA-Z0-9\s\-_.()]+$/;
  if (!validPattern.test(trimmedName)) {
    return "Coin Name contains invalid characters";
  }

  return null;
};

export const encryptResponse = async (data) => {
  const bytes = CryptoJS.AES.decrypt(data, "paraacryptobackendnodejs");
  const decryptedText = bytes.toString(CryptoJS.enc.Utf8);

  const parsedData = JSON.parse(decryptedText);

  return parsedData;
};

export const decryptString = (data) => {
  const jsonData = JSON.stringify(data);
  const token = CryptoJS.AES.encrypt(
    jsonData,
    "paraacryptobackendnodejs"
  ).toString();
  return token;
};
